import { defineStore } from 'pinia'
import { ref } from 'vue'

import i18n from '@/libs/i18n'
import { UserRole } from '@/enums/common'
import type { IMeResponse } from '@/types/Auth'
import type { IUser } from '@/types/User'

export const useAppStore = defineStore('app', () => {
    const JWT = ref(localStorage.getItem('JWT') || '')
    const refreshToken = ref(localStorage.getItem('refresh_token') || '')
    const locale = ref(localStorage.getItem('locale') || 'sl')
    const dark = ref(localStorage.getItem('dark') === '1')
    const windowWidth = ref(window.innerWidth)
    const loaderActive = ref(0)

    const userData = ref<IUser>()
    const projectIds = ref<string[]>([])

    const getJWT = () => JWT.value
    const isLoggedIn = () => !!JWT.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile = () => windowWidth.value < 1024
    const isAdmin = () => userData.value?.role === UserRole.Admin

    // Sets a JWT cookie so API requests for streamed files (loaded via <img src>/<a href>) are
    // authenticated — browsers cannot set Authorization headers for those.
    const setJWTCookie = (jwt: string) => {
        if (jwt) {
            document.cookie = `JWT=${jwt}; path=/; SameSite=Lax`
        } else {
            document.cookie = 'JWT=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax'
        }
    }

    setJWTCookie(JWT.value)
    applyDark(dark.value)

    const setLogin = (jwt: string, refresh: string) => {
        localStorage.setItem('JWT', jwt)
        localStorage.setItem('refresh_token', refresh)
        JWT.value = jwt
        refreshToken.value = refresh
        setJWTCookie(jwt)
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        localStorage.removeItem('refresh_token')
        JWT.value = ''
        refreshToken.value = ''
        userData.value = undefined
        projectIds.value = []
        setJWTCookie('')
    }

    const setUser = (me: IMeResponse) => {
        userData.value = me.user
        projectIds.value = me.project_ids ?? []
    }

    const startLoading = () => loaderActive.value++
    const stopLoading = () => (loaderActive.value = Math.max(0, loaderActive.value - 1))

    const changeLocale = (newLocale: string) => {
        locale.value = newLocale
        localStorage.setItem('locale', newLocale)
        ;(i18n.global.locale as unknown as { value: string }).value = newLocale
    }

    const toggleDark = () => {
        dark.value = !dark.value
        localStorage.setItem('dark', dark.value ? '1' : '0')
        applyDark(dark.value)
    }

    return {
        JWT,
        refreshToken,
        locale,
        dark,
        userData,
        projectIds,
        loaderActive,
        getJWT,
        isLoggedIn,
        isLoaderActive,
        isMobile,
        isAdmin,
        setLogin,
        setLogout,
        setUser,
        startLoading,
        stopLoading,
        changeLocale,
        toggleDark,
    }
})

function applyDark(on: boolean) {
    document.documentElement.classList.toggle('dark', on)
}
